import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Clock, Share2, Linkedin, Twitter } from 'lucide-react';
import { blogPosts } from '../data/mock';
import SEO from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  
  // Redirect to dedicated interactive blog page
  if (slug === 'options-strategies-complete-guide') {
    return <Navigate to="/blog/options-strategies-complete-guide" replace />;
  }
  
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div style={{ paddingTop: '80px' }}>
        <section style={{
          padding: '120px 0',
          textAlign: 'center'
        }}>
          <div className="container">
            <h1 className="heading-2" style={{ marginBottom: '24px' }}>Post Not Found</h1>
            <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
              The blog post you're looking for doesn't exist.
            </p>
            <Link to="/blog" className="btn-primary">
              <ArrowLeft size={20} /> Back to Blog
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Estimate read time based on content length
  const wordCount = post.content ? post.content.split(/\s+/).length : 200;
  const readTime = Math.max(3, Math.ceil(wordCount / 200));

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title={`${post.title} | Vitta Finance Academy Blog`}
        description={post.excerpt}
        keywords={post.keywords || post.tags.join(', ')}
        ogTitle={post.title}
        ogDescription={post.excerpt}
      />

      {/* Hero Section */}
      <section style={{
        padding: '100px 0 60px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        position: 'relative'
      }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {/* Breadcrumb */}
            <div style={{ marginBottom: '24px' }}>
              <Link to="/blog" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'rgba(255,255,255,0.8)',
                textDecoration: 'none',
                fontSize: '0.95rem',
                transition: 'color 0.3s ease'
              }}>
                <ArrowLeft size={18} /> Back to Blog
              </Link>
            </div>

            {/* Tags */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginBottom: '20px'
            }}>
              {post.tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} style={{
                  padding: '6px 14px',
                  backgroundColor: 'rgba(212, 175, 55, 0.2)',
                  border: '1px solid var(--gold-primary)',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  color: 'var(--gold-primary)',
                  fontWeight: 500
                }}>
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              {post.title}
            </h1>

            {/* Meta Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              flexWrap: 'wrap',
              color: 'rgba(255,255,255,0.9)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} />
                <span>{post.date}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={18} />
                <span>{readTime} min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section style={{
        padding: '80px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div className="blog-layout" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 300px',
            gap: '60px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Main Content */}
            <article style={{
              backgroundColor: 'var(--bg-card)',
              padding: '48px',
              borderRadius: '16px',
              border: '1px solid var(--border-light)'
            }}>
              {/* Excerpt/Introduction */}
              <p style={{
                fontSize: '1.2rem',
                lineHeight: 1.8,
                color: 'var(--text-secondary)',
                marginBottom: '32px',
                paddingBottom: '32px',
                borderBottom: '1px solid var(--border-light)',
                fontStyle: 'italic'
              }}>
                {post.excerpt}
              </p>

              {/* Article Body */}
              {post.content ? (
                <div 
                  className="blog-content"
                  style={{
                    fontSize: '1.05rem',
                    lineHeight: 1.9,
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              ) : (
                <div style={{
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                  color: 'var(--text-primary)'
                }}>
                  <p>Full article coming soon. Stay tuned for comprehensive insights on this topic.</p>
                  <p style={{ marginTop: '24px' }}>
                    In the meantime, explore our <Link to="/courses" style={{ color: 'var(--brand-primary)', textDecoration: 'underline' }}>courses</Link> and 
                    <Link to="/books" style={{ color: 'var(--brand-primary)', textDecoration: 'underline', marginLeft: '4px' }}>study materials</Link> to start your CIRO exam preparation.
                  </p>
                </div>
              )}

              {/* Tags Section */}
              <div style={{
                marginTop: '48px',
                paddingTop: '32px',
                borderTop: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <Tag size={18} color="var(--brand-primary)" />
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Tags:</span>
                  {post.tags.map((tag, idx) => (
                    <span key={idx} style={{
                      padding: '6px 14px',
                      backgroundColor: 'var(--secondary-olive)',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      color: 'var(--text-secondary)'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Share Card */}
              <div style={{
                backgroundColor: 'var(--bg-card)',
                padding: '28px',
                borderRadius: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Share2 size={20} color="var(--brand-primary)" /> Share This Article
                </h3>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <a 
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '44px',
                      height: '44px',
                      backgroundColor: '#0077b5',
                      borderRadius: '8px',
                      color: 'white',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <Linkedin size={20} />
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '44px',
                      height: '44px',
                      backgroundColor: '#1da1f2',
                      borderRadius: '8px',
                      color: 'white',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <Twitter size={20} />
                  </a>
                </div>
              </div>

              {/* CTA Card */}
              <div style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                padding: '28px',
                borderRadius: '16px',
                color: 'white'
              }}>
                <h3 style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  marginBottom: '16px'
                }}>
                  Ready to Pass Your CIRO Exam?
                </h3>
                <p style={{
                  fontSize: '0.95rem',
                  lineHeight: 1.7,
                  marginBottom: '20px',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  Get expert-led courses, comprehensive study guides, and mock exams.
                </p>
                <a 
                  href="https://www.vittafinance.ca/store" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-gold"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  Browse Courses
                </a>
              </div>

              {/* Related Posts */}
              <div style={{
                backgroundColor: 'var(--bg-card)',
                padding: '28px',
                borderRadius: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  marginBottom: '20px'
                }}>
                  Related Articles
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {blogPosts
                    .filter(p => p.id !== post.id)
                    .slice(0, 3)
                    .map(relatedPost => (
                      <Link 
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.slug}`}
                        className="related-article-link"
                        style={{
                          textDecoration: 'none',
                          padding: '12px',
                          backgroundColor: 'var(--bg-page)',
                          borderRadius: '8px',
                          display: 'block'
                        }}
                      >
                        <h4 style={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '6px',
                          lineHeight: 1.4
                        }}>
                          {relatedPost.title.length > 60 
                            ? relatedPost.title.substring(0, 60) + '...' 
                            : relatedPost.title}
                        </h4>
                        <span style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)'
                        }}>
                          {relatedPost.date}
                        </span>
                      </Link>
                    ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{
        padding: '80px 0',
        background: 'var(--secondary-olive)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-3" style={{ marginBottom: '16px' }}>
            Start Your CIRO Exam Journey Today
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '32px',
            maxWidth: '600px',
            margin: '0 auto 32px',
            color: 'var(--text-secondary)'
          }}>
            Join thousands of successful candidates who passed their exams with Vitta Finance Academy.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://www.vittafinance.ca/store" target="_blank" rel="noopener noreferrer" className="btn-primary">
              View Courses
            </a>
            <Link to="/books" className="btn-secondary">
              Browse Books
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Content Styles */}
      <style>{`
        .blog-content h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--brand-primary);
          margin-top: 40px;
          margin-bottom: 20px;
        }
        .blog-content h3 {
          font-size: 1.35rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-top: 32px;
          margin-bottom: 16px;
        }
        .blog-content p {
          margin-bottom: 20px;
        }
        .blog-content ul, .blog-content ol {
          margin-bottom: 24px;
          padding-left: 24px;
        }
        .blog-content li {
          margin-bottom: 12px;
          line-height: 1.7;
        }
        .blog-content strong {
          color: var(--text-primary);
          font-weight: 600;
        }
        .blog-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 24px 0;
        }
        .blog-content th, .blog-content td {
          padding: 12px 16px;
          text-align: left;
          border: 1px solid var(--border-light);
        }
        .blog-content th {
          background-color: var(--brand-primary);
          color: white;
          font-weight: 600;
        }
        .blog-content tr:nth-child(even) {
          background-color: var(--bg-page);
        }
        @media (max-width: 900px) {
          .blog-layout {
            grid-template-columns: 1fr !important;
          }
          .blog-layout article {
            padding: 24px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogPost;
